import { formatDistanceToNow } from 'date-fns';
import { Link, useNavigate } from 'react-router';
import { Check, CheckCheck, MoreHorizontal } from 'lucide-react';
import {
  type Incident,
  type IncidentStatus,
  useAcknowledgeIncident,
  useRegionIncidents,
  useResolveIncident,
} from '@/hooks/useIncidents';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { SeverityBadge } from '@/components/SeverityBadge.tsx';

const STATUS_STYLE: Record<IncidentStatus, string> = {
  created: 'bg-sev-2/15 text-sev-2',
  acknowledged: 'bg-sev-5/15 text-sev-5',
  resolved: 'bg-sev-4/15 text-sev-4',
  archived: 'bg-muted text-muted-foreground',
};

interface RegionIncidentsProps {
  regionId: string;
}

/**
 * Open incidents scoped to a single region. Embedded in
 * RegionDetails > Incidents tab. Same sev/status palette and
 * row-actions pattern as the main Incidents list.
 */
export default function RegionIncidents({ regionId }: RegionIncidentsProps) {
  const navigate = useNavigate();
  const { data: incidents, isLoading } = useRegionIncidents(regionId);
  const acknowledgeMutation = useAcknowledgeIncident();
  const resolveMutation = useResolveIncident();

  if (isLoading) {
    return <Skeleton className="h-32 w-full" />;
  }

  if (!incidents || incidents.length === 0) {
    return (
      <EmptyState
        variant="no-data"
        title="No open incidents"
        description="Nothing to triage in this region."
        size="inline"
      />
    );
  }

  const columns: readonly ResponsiveTableColumn<Incident>[] = [
    {
      key: 'title',
      label: 'Incident',
      hideMobileLabel: true,
      render: (i) => (
        <div className="min-w-0">
          <Link
            to={`/incidents/${i.id}`}
            className="font-medium hover:underline truncate block"
            onClick={(e) => { e.stopPropagation(); }}
          >
            {i.title}
          </Link>
          {i.description ? (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
              {i.description}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'severity',
      label: 'Severity',
      render: (i) => (
        <SeverityBadge severity={i.severity} />
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (i) => (
        <Badge className={cn('border-0 font-normal capitalize', STATUS_STYLE[i.status])}>
          {i.status}
        </Badge>
      ),
    },
    {
      key: 'source',
      label: 'Source',
      hideAt: 'mobile',
      render: (i) => (
        <span className="text-xs text-muted-foreground">
          {i.source === 'automated' ? 'Auto' : 'Manual'}
        </span>
      ),
    },
    {
      key: 'created',
      label: 'Created',
      hideAt: 'mobile',
      render: (i) => (
        <span className="text-xs text-muted-foreground">
          {formatDistanceToNow(new Date(i.createdAt), { addSuffix: true })}
        </span>
      ),
    },
  ];

  return (
    <ResponsiveTable<Incident>
      columns={columns}
      rows={incidents}
      rowKey={(r) => r.id}
      onRowClick={(r) => { void navigate(`/incidents/${r.id}`); }}
      rowActions={(incident) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => { e.stopPropagation(); }}
              aria-label={`Actions for ${incident.title}`}
            >
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => { e.stopPropagation(); }}>
            {incident.status === 'created' ? (
              <DropdownMenuItem
                onClick={() => { acknowledgeMutation.mutate(incident.id); }}
                disabled={
                  acknowledgeMutation.isPending &&
                  acknowledgeMutation.variables === incident.id
                }
                className="gap-2"
              >
                <Check className="w-4 h-4" />
                Acknowledge
              </DropdownMenuItem>
            ) : null}
            {incident.status === 'created' || incident.status === 'acknowledged' ? (
              <DropdownMenuItem
                onClick={() => { resolveMutation.mutate(incident.id); }}
                disabled={
                  resolveMutation.isPending && resolveMutation.variables === incident.id
                }
                className="gap-2"
              >
                <CheckCheck className="w-4 h-4" />
                Resolve
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    />
  );
}
